import React, { useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function PricingAdmin({ s, setS }) {
  const { t } = useApp();
  const [newRoute, setNewRoute] = useState({});
  const [newSvc, setNewSvc] = useState("");
  const [newVeh, setNewVeh] = useState({ label: "", capacity: 4 });
  const services = s.services || {}, vt = s.vehicle_types || {};
  const vkeys = Object.keys(vt);
  const setPrice = (k, v) => setS({ ...s, prices: { ...s.prices, [k]: v === "" ? undefined : Number(v) } });

  const addRoute = (sk) => {
    const r = (newRoute[sk] || "").trim(); if (!r) return;
    if (services[sk].routes.includes(r)) return toast.error(t("route_exists"));
    setS({ ...s, services: { ...services, [sk]: { ...services[sk], routes: [...services[sk].routes, r] } } });
    setNewRoute({ ...newRoute, [sk]: "" });
  };
  const removeRoute = (sk, r) => {
    if (!window.confirm(`${t("delete")} "${r}"?`)) return;
    setS({ ...s, services: { ...services, [sk]: { ...services[sk], routes: services[sk].routes.filter((x) => x !== r) } } });
  };
  const addService = () => {
    const label = newSvc.trim(); if (!label) return;
    const k = slug(label); if (services[k]) return toast.error(t("service_exists"));
    setS({ ...s, services: { ...services, [k]: { label, routes: [] } } }); setNewSvc("");
  };
  const removeService = (sk) => { if (window.confirm(`${t("delete")} ${services[sk].label}?`)) { const n = { ...services }; delete n[sk]; setS({ ...s, services: n }); } };
  const addVehicle = () => {
    const label = newVeh.label.trim(); if (!label) return;
    const k = slug(label); if (vt[k]) return toast.error(t("vehicle_exists"));
    setS({ ...s, vehicle_types: { ...vt, [k]: { label, capacity: Number(newVeh.capacity) || 4 } } }); setNewVeh({ label: "", capacity: 4 });
  };
  const removeVehicle = (vk) => { if (window.confirm(`${t("delete")} ${vt[vk].label}?`)) { const n = { ...vt }; delete n[vk]; setS({ ...s, vehicle_types: n }); } };
  const setVehicle = (vk, patch) => setS({ ...s, vehicle_types: { ...vt, [vk]: { ...vt[vk], ...patch } } });

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-4 grid md:grid-cols-2 gap-4">
        <div><Label>{t("dp")} (%)</Label><Input type="number" value={s.dp_percent} onChange={(e) => setS({ ...s, dp_percent: +e.target.value })} data-testid="set-dp" /></div>
        <div><Label>{t("idr_rate")} (IDR / 1 SAR)</Label><Input type="number" value={s.idr_rate} onChange={(e) => setS({ ...s, idr_rate: +e.target.value })} data-testid="set-rate" /></div>
      </CardContent></Card>
      <Card><CardHeader><CardTitle className="text-base">{t("vehicle_types")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {vkeys.map((vk) => (
            <div key={vk} className="flex flex-wrap items-center gap-2" data-testid={`vt-row-${vk}`}>
              <span className="font-mono text-xs text-slate-400 w-20">{vk}</span>
              <Input className="h-8 w-44" value={vt[vk].label} onChange={(e) => setVehicle(vk, { label: e.target.value })} data-testid={`vt-label-${vk}`} />
              <Input className="h-8 w-20" type="number" value={vt[vk].capacity} onChange={(e) => setVehicle(vk, { capacity: +e.target.value })} data-testid={`vt-cap-${vk}`} /><span className="text-xs text-slate-500">pax</span>
              <Button size="sm" variant="ghost" onClick={() => removeVehicle(vk)} data-testid={`vt-del-${vk}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
            </div>))}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
            <Input className="h-8 w-44" placeholder={t("vehicle_name")} value={newVeh.label} onChange={(e) => setNewVeh({ ...newVeh, label: e.target.value })} data-testid="vt-new-label" />
            <Input className="h-8 w-20" type="number" value={newVeh.capacity} onChange={(e) => setNewVeh({ ...newVeh, capacity: e.target.value })} data-testid="vt-new-cap" />
            <Button size="sm" variant="outline" onClick={addVehicle} data-testid="vt-add"><Plus className="w-4 h-4 mr-1" />{t("add_vehicle_type")}</Button>
          </div>
        </CardContent></Card>
      {Object.entries(services).map(([sk, svc]) => (
        <Card key={sk} data-testid={`svc-card-${sk}`}><CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base"><Input className="h-8 w-64 font-semibold" value={svc.label} onChange={(e) => setS({ ...s, services: { ...services, [sk]: { ...svc, label: e.target.value } } })} data-testid={`svc-label-${sk}`} /></CardTitle>
          <Button size="sm" variant="ghost" onClick={() => removeService(sk)} data-testid={`svc-del-${sk}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">{t("route")}</th>{vkeys.map((v) => <th key={v} className="py-2 px-1">{vt[v].label}</th>)}<th /></tr></thead>
              <tbody>{svc.routes.map((r, ri) => (
                <tr key={r} className="border-t" data-testid={`route-row-${sk}-${ri}`}><td className="py-2 font-medium whitespace-nowrap pr-3">{r}</td>
                  {vkeys.map((v) => { const k = `${sk}|${r}|${v}`;
                    return <td key={v} className="py-1 px-1"><Input type="number" className="h-8 w-24" placeholder="-" value={s.prices[k] ?? ""} onChange={(e) => setPrice(k, e.target.value)} data-testid={`price-${sk}-${v}-${ri}`} /></td>; })}
                  <td><Button size="sm" variant="ghost" onClick={() => removeRoute(sk, r)} data-testid={`route-del-${sk}-${ri}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button></td>
                </tr>))}
                <tr className="border-t"><td colSpan={vkeys.length + 2} className="py-2">
                  <div className="flex gap-2"><Input className="h-8 max-w-sm" placeholder={t("new_route_ph")} value={newRoute[sk] || ""} onChange={(e) => setNewRoute({ ...newRoute, [sk]: e.target.value })} data-testid={`route-new-${sk}`} />
                    <Button size="sm" variant="outline" onClick={() => addRoute(sk)} data-testid={`route-add-${sk}`}><Plus className="w-4 h-4 mr-1" />{t("add_route")}</Button></div></td></tr>
              </tbody>
            </table>
          </CardContent></Card>))}
      <Card><CardContent className="p-4 flex flex-wrap gap-2 items-center">
        <Input className="h-8 w-64" placeholder={t("new_service_ph")} value={newSvc} onChange={(e) => setNewSvc(e.target.value)} data-testid="svc-new" />
        <Button size="sm" variant="outline" onClick={addService} data-testid="svc-add"><Plus className="w-4 h-4 mr-1" />{t("add_service")}</Button>
        <span className="text-xs text-slate-500">{t("pricing_hint")}</span>
      </CardContent></Card>
    </div>
  );
}
